import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import {
  NT8_OVERLAY_WEEKDAYS,
  NT8_OVERLAY_DAY_KEYS,
  MAX_NT8_OVERLAY_SLEEVES,
  makeEmptyNt8OverlaySleeve,
} from '../utils/nt8OverlayConfig';
import { IconPlus, IconTrash } from './Icons';
import './VectorCapitalPlusSimulator.css';

/**
 * Shared NT8 sleeve editor (strategy + weekday chips + size multiplier).
 * Used by the Strategy Settings lightning editor AND the Webhooks → Mappings
 * tab — one editor, one validation path (utils/nt8OverlayConfig).
 */

export function useNt8AlertStrategies(active) {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const resp = await fetch(API_ENDPOINTS.adminNt8AlertStrategies, {
          headers: getBearerAuthHeaders(),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.success) {
          throw new Error(data.error || 'Failed to load NT8 strategies');
        }
        if (!cancelled) {
          setStrategies(Array.isArray(data.strategies) ? data.strategies : []);
        }
      } catch (e) {
        if (!cancelled) {
          setStrategies([]);
          setLoadError(e.message || 'Failed to load NT8 strategies');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  return { strategies, loading, loadError };
}

export function Nt8OverlaySleeveBuilder({
  sleeves,
  onSleevesChange,
  strategyOptions,
  loading,
  loadError,
  mappedFrom,
}) {
  const updateSleeve = (idx, patch) => {
    onSleevesChange(sleeves.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const toggleDay = (idx, dayKey) => {
    onSleevesChange(sleeves.map((s, i) => {
      if (i !== idx) return s;
      const has = s.days.includes(dayKey);
      const nextDays = has ? s.days.filter((d) => d !== dayKey) : [...s.days, dayKey];
      return { ...s, days: NT8_OVERLAY_DAY_KEYS.filter((d) => nextDays.includes(d)) };
    }));
  };

  const addSleeve = () => {
    if (sleeves.length >= MAX_NT8_OVERLAY_SLEEVES) return;
    onSleevesChange([...sleeves, makeEmptyNt8OverlaySleeve()]);
  };

  const removeSleeve = (idx) => {
    if (sleeves.length <= 1) return;
    onSleevesChange(sleeves.filter((_, i) => i !== idx));
  };

  return (
    <div className="veps-nt8-sleeves veps-span-2">
      <div className="veps-nt8-sleeves-head">
        <span>Live NT8 strategies (from webhooks)</span>
        <small>Pick strategy names that appear in incoming NT8 Trade Alert webhooks. Combine multiple with weekday schedules.</small>
      </div>
      {loading ? <small>Loading strategies from NT8 alerts…</small> : null}
      {loadError ? <small className="veps-status err">{loadError}</small> : null}
      {!loading && !strategyOptions.length ? (
        <small>No NT8 Trade Alert strategies yet — send a test webhook first.</small>
      ) : null}
      <div className="veps-nt8-sleeve-matrix">
        <div className="veps-nt8-sleeve-matrix-header">
          <div className="veps-nt8-sleeve-col-strategy">Strategy</div>
          {NT8_OVERLAY_WEEKDAYS.map((d) => (
            <div key={d.key} className="veps-nt8-sleeve-col-day">{d.short}</div>
          ))}
          <div className="veps-nt8-sleeve-col-size" title="Contract size multiplier">Size</div>
          <div className="veps-nt8-sleeve-col-remove" aria-hidden="true" />
        </div>
        {sleeves.map((sleeve, idx) => (
          <div key={idx} className="veps-nt8-sleeve-row">
            <div className="veps-nt8-sleeve-col-strategy">
              <select
                value={sleeve.strategyName}
                onChange={(e) => updateSleeve(idx, { strategyName: e.target.value })}
                aria-label={`NT8 sleeve ${idx + 1} strategy`}
              >
                <option value="">Select strategy…</option>
                {strategyOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            {NT8_OVERLAY_WEEKDAYS.map((d) => (
              <div key={d.key} className="veps-nt8-sleeve-col-day">
                <label
                  className={`veps-nt8-sleeve-day ${sleeve.days.includes(d.key) ? 'is-active' : ''}`}
                  title={`${sleeve.strategyName || 'Strategy'} on ${d.key}`}
                >
                  <input
                    type="checkbox"
                    checked={sleeve.days.includes(d.key)}
                    onChange={() => toggleDay(idx, d.key)}
                    aria-label={`${d.short} for sleeve ${idx + 1}`}
                  />
                  <span>{d.short}</span>
                </label>
              </div>
            ))}
            <div className="veps-nt8-sleeve-col-size">
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={sleeve.size}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateSleeve(idx, { size: '' });
                    return;
                  }
                  const n = parseInt(raw, 10);
                  if (!Number.isFinite(n)) return;
                  updateSleeve(idx, { size: Math.min(Math.max(n, 1), 100) });
                }}
                aria-label={`Sleeve ${idx + 1} size multiplier`}
              />
              <span>×</span>
            </div>
            <div className="veps-nt8-sleeve-col-remove">
              <button
                type="button"
                className="veps-nt8-sleeve-remove"
                onClick={() => removeSleeve(idx)}
                disabled={sleeves.length <= 1}
                aria-label={`Remove sleeve ${idx + 1}`}
              >
                <IconTrash size={14} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="veps-nt8-sleeve-add"
          onClick={addSleeve}
          disabled={sleeves.length >= MAX_NT8_OVERLAY_SLEEVES}
        >
          <IconPlus size={14} /> Add another strategy
        </button>
      </div>
      {mappedFrom ? (
        <p className="veps-scenario-hint">
          Live NT8 trades from <strong>{mappedFrom}</strong> forward (or after your last CSV trade, whichever is later).
          Changing strategies or weekdays resets the connect date.
        </p>
      ) : (
        <p className="veps-scenario-hint">
          Trades must match a mapped strategy name in the webhook alert and fall on an active weekday (ET).
        </p>
      )}
    </div>
  );
}

export default Nt8OverlaySleeveBuilder;
